import JSPDF from "jspdf";
import "jspdf-autotable";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  AlignmentType,
  WidthType,
  TableLayoutType,
  VerticalAlign,
  PageNumber,
  Footer,
} from "docx";
import { saveAs } from "file-saver";
import { Toast, getFormattedDateString } from "utility/helpers";

const formatDateForFooter = (date) => {
  if (!date) return "";

  return getFormattedDateString(date, "MMM. dd, yyyy");
};

/**
 * Transform compiled export API response into the structure expected by
 * PDF/DOCx generators. API returns array of { header, body, footer }:
 * - header: congressman (id, fullName, district)
 * - body: [{ position, committee }] — order from backend (no frontend sort)
 * - footer: [{ source, dateElected }] — aligned by index with body
 */
const transformCompiledExportData = (data) => {
  const list = Array.isArray(data)
    ? data
    : (data?.data || data?.rows || data?.items || []);

  if (!Array.isArray(list)) {
    return [];
  }

  return list
    .filter((item) => item?.header?.id && item?.header?.fullName)
    .map((item) => {
      const body = item.body || [];
      const footer = item.footer || [];

      const committees = body.map((row, i) => {
        const f = footer[i] || {};
        const committee = row?.committee ? { name: row.committee.name } : { name: "-" };
        const position = row?.position ? { name: row.position.name } : { name: "Member" };

        return {
          committee,
          position,
          source: f?.source,
          dateElected: f?.dateElected,
        };
      });

      const district =
        item.header?.district?.name ||
        item.header?.district?.region?.name ||
        "";

      return {
        houseMember: {
          id: item.header.id,
          name: item.header.fullName,
          district,
        },
        committees,
      };
    });
};

export const generateCommitteePDF = (data, congressNumber = "20th") => {
  try {
    const groupedData = transformCompiledExportData(data);

    if (!groupedData || groupedData.length === 0) {
      Toast({
        type: 3,
        content: "No data available to export.",
      });

      return;
    }

    // Generate PDF for each house member
    groupedData.forEach((memberData, memberIndex) => {
      const doc = new JSPDF("portrait", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      let yPosition = 30;

      // Document Title: Times New Roman 16pt BOLD
      doc.setFont("times", "bold");
      doc.setFontSize(16);

      doc.text("COMMITTEE MEMBERSHIP", pageWidth / 2, yPosition, {
        align: "center",
      });

      yPosition += 10;

      // "20th Congress": Times New Roman 12pt BOLD
      doc.setFont("times", "bold");
      doc.setFontSize(12);

      doc.text(`${congressNumber} Congress`, pageWidth / 2, yPosition, {
        align: "center",
      });

      yPosition += 12;

      // House Member: Times New Roman 16pt BOLD
      doc.setFont("times", "bold");
      doc.setFontSize(16);

      doc.text(
        memberData.houseMember.name,
        pageWidth / 2,
        yPosition,
        {
          align: "center",
        }
      );

      yPosition += 10;

      // District/Partylist: Times New Roman 12pt BOLD ITALIC
      doc.setFont("times", "bolditalic");
      doc.setFontSize(12);

      doc.text(
        memberData.houseMember.district,
        pageWidth / 2,
        yPosition,
        {
          align: "center",
        }
      );

      yPosition += 15;

      // Prepare table data with superscripts for footer notes
      const tableData = [];
      const footerNotes = [];
      const positionData = []; // Store position info for custom rendering
      let noteCounter = 0; // Sequential counter for entries with source

      memberData.committees.forEach((committee) => {
        const committeeName = committee?.committee?.name || "-";
        const positionName = committee?.position?.name || committee?.position || "-";
        const source = committee?.source || "";
        const dateElected = committee?.dateElected;

        // Only add superscript if source is not empty or null
        const hasSource = source && source.trim() !== "";
        let noteNumber = null;

        if (hasSource) {
          noteCounter += 1;
          noteNumber = noteCounter;
        }

        // Store position data for custom rendering
        positionData.push({ positionName, noteNumber, hasSource });

        // Second column placeholder so didDrawCell runs for Position (index 1)
        tableData.push([committeeName, ""]);

        // Add footer note only if source exists
        // Store as object for custom rendering with superscript
        if (hasSource && noteNumber !== null) {
          footerNotes.push({
            noteNumber,
            source,
            dateElected,
          });
        }
      });

      // Add table - Body: Times New Roman 12pt
      doc.setFont("times", "normal");
      doc.setFontSize(12);

      doc.autoTable({
        startY: yPosition,
        head: [["Committee", "Position"]],
        body: tableData,
        theme: "plain",
        styles: {
          fontSize: 12,
          font: "times",
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: 0,
          fontStyle: "bold",
          fontSize: 12,
        },
        columnStyles: {
          0: { cellWidth: pageWidth * 0.6 - 20 },
          1: { cellWidth: pageWidth * 0.4 - 20 },
        },
        margin: { left: 20, right: 20 },
        didDrawCell: (cellData) => {
          // Custom rendering for position column with superscript
          if (cellData.column.index === 1 && cellData.section === "body") {
            const rowIndex = cellData.row.index;
            const posData = positionData[rowIndex];

            if (posData) {
              // Clear the default text
              doc.setTextColor(255, 255, 255);
              doc.text("", cellData.cell.x, cellData.cell.y);

              // Draw position name - Times New Roman 12pt
              doc.setFont("times", "normal");
              doc.setFontSize(12);
              doc.setTextColor(0, 0, 0);

              const textWidth = doc.getTextWidth(posData.positionName);

              doc.text(
                posData.positionName,
                cellData.cell.x + cellData.cell.padding("left"),
                cellData.cell.y +
                  cellData.cell.height -
                  cellData.cell.padding("bottom")
              );

              // Superscript number only if source exists (e.g. Chairperson¹)
              if (posData.hasSource && posData.noteNumber !== null) {
                doc.setFont("times", "normal");
                doc.setFontSize(8); // Smaller for superscript

                const superscriptY =
                  cellData.cell.y +
                  cellData.cell.height -
                  cellData.cell.padding("bottom") -
                  2; // Raise it up

                doc.text(
                  String(posData.noteNumber),
                  cellData.cell.x +
                    cellData.cell.padding("left") +
                    textWidth +
                    1,
                  superscriptY
                );
              }
            }
          }
        },
      });

      // Position footer notes at the bottom of the page
      if (footerNotes.length > 0) {
        // Calculate footer position - always at bottom
        // Approximate height per note
        const footerNoteHeight = footerNotes.length * 5;
        const footerStartY = pageHeight - 20 - footerNoteHeight;
        // Line position before footer
        const lineY = footerStartY - 5; // Add space after line

        // Draw horizontal line before footer (1/4 of page width)
        const lineStartX = 20;
        const lineEndX = 20 + pageWidth / 4; // 1/4 of page width
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(lineStartX, lineY, lineEndX, lineY);

        // Footer: ¹ source (date elected) - Calibri 9pt (use times as fallback)
        doc.setFont("times", "normal");
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);

        let footerYPos = footerStartY;

        footerNotes.forEach((noteData) => {
          // Check if we need a new page
          if (footerYPos > pageHeight - 20) {
            doc.addPage();

            // Recalculate for new page
            const newFooterStartY = pageHeight - 20 - footerNoteHeight;
            const newLineY = newFooterStartY - 5;

            // Draw line on new page
            doc.line(lineStartX, newLineY, lineEndX, newLineY);

            footerYPos = newFooterStartY;
          }

          const xPos = 20;

          // Superscript number (e.g. ¹)
          doc.setFontSize(7);
          const superscriptY = footerYPos - 1.5;
          doc.text(String(noteData.noteNumber), xPos, superscriptY);

          // " source (date elected)" - 9pt
          doc.setFontSize(9);

          const dateStr = noteData.dateElected
            ? `(${formatDateForFooter(noteData.dateElected)})`
            : "";

          const sourceText = `${noteData.source} ${dateStr}`.trim();

          const numberWidth = doc.getTextWidth(String(noteData.noteNumber));

          const textToDraw = sourceText ? ` ${sourceText}` : "";

          doc.text(textToDraw, xPos + numberWidth + 1, footerYPos, {
            maxWidth: pageWidth - 40 - numberWidth - 2,
          });

          footerYPos += 5;
        });
      }

      // Save the PDF - generate separate file for each house member
      const memberName = memberData.houseMember.name.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      );
      const dateStr = new Date().toISOString().split("T")[0];
      const fileName = `Committee_Membership_${memberName}_${dateStr}.pdf`;

      // Add a small delay to prevent browser from blocking multiple downloads
      setTimeout(() => {
        doc.save(fileName);
      }, memberIndex * 200);
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    Toast({ type: 4, content: "Failed to generate PDF file." });
  }
};

export const generateCommitteeDOCx = async (data, congressNumber = "20th") => {
  try {
    const groupedData = transformCompiledExportData(data);

    if (!groupedData || groupedData.length === 0) {
      Toast({
        type: 3,
        content: "No data available to export.",
      });

      return;
    }

    // Generate DOCx for each house member - process sequentially with delays
    // to prevent browser from blocking multiple downloads
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < groupedData.length; i += 1) {
      const memberData = groupedData[i];
      const children = [];

      // Document Title: "COMMITTEE MEMBERSHIP" - Times New Roman 16pt BOLD
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "COMMITTEE MEMBERSHIP",
              bold: true,
              size: 32, // 16pt = 32 half-points
              font: "Times New Roman",
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        })
      );

      // "20th Congress" - Times New Roman 12pt BOLD
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${congressNumber} Congress`,
              bold: true,
              size: 24, // 12pt = 24 half-points
              font: "Times New Roman",
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 240 },
        })
      );

      // House Member - Times New Roman 16pt BOLD
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: memberData.houseMember.name,
              bold: true,
              size: 32, // 16pt = 32 half-points
              font: "Times New Roman",
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        })
      );

      // District/Partylist - Times New Roman 12pt BOLD ITALIC
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: memberData.houseMember.district,
              bold: true,
              italics: true,
              size: 24, // 12pt = 24 half-points
              font: "Times New Roman",
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Prepare table data with superscripts for footer notes
      const tableRows = [];
      const footerNotes = [];
      let noteCounter = 0; // Sequential counter for entries with source

      memberData.committees.forEach((committee) => {
        const committeeName = committee?.committee?.name || "-";
        const positionName = committee?.position?.name || committee?.position || "-";
        const source = committee?.source || "";
        const dateElected = committee?.dateElected;

        // Only add superscript if source is not empty or null
        const hasSource = source && source.trim() !== "";
        let noteNumber = null;

        if (hasSource) {
          noteCounter += 1;
          noteNumber = noteCounter;
        }

        // Create position text with superscript (only if source exists)
        const positionText = [];

        positionText.push(
          new TextRun({
            text: positionName,
            size: 24, // 12pt
            font: "Times New Roman",
          })
        );

        if (hasSource && noteNumber !== null) {
          positionText.push(
            new TextRun({
              text: String(noteNumber),
              size: 14, // 7pt for superscript (smaller)
              font: "Times New Roman",
              superScript: true,
            })
          );
        }

        tableRows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: committeeName,
                        size: 24, // 12pt
                        font: "Times New Roman",
                      }),
                    ],
                  }),
                ],
                verticalAlign: VerticalAlign.CENTER,
                width: { size: 60, type: WidthType.PERCENTAGE },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: positionText,
                  }),
                ],
                verticalAlign: VerticalAlign.CENTER,
                width: { size: 40, type: WidthType.PERCENTAGE },
              }),
            ],
          })
        );

        // Add footer note only if source exists
        // Store as object for custom rendering with superscript
        if (hasSource && noteNumber !== null) {
          footerNotes.push({
            noteNumber,
            source,
            dateElected,
          });
        }
      });

      // Create table with header row and no borders
      const allTableRows = [
        // Header row
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Committee",
                      bold: true,
                      size: 24, // 12pt
                      font: "Times New Roman",
                    }),
                  ],
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
              width: { size: 60, type: WidthType.PERCENTAGE },
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Position",
                      bold: true,
                      size: 24, // 12pt
                      font: "Times New Roman",
                    }),
                  ],
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
              width: { size: 40, type: WidthType.PERCENTAGE },
            }),
          ],
        }),
        ...tableRows,
      ];

      children.push(
        new Table({
          rows: allTableRows,
          width: { size: 100, type: WidthType.PERCENTAGE },
          layout: TableLayoutType.FIXED,
          borders: {
            top: { style: "none" },
            bottom: { style: "none" },
            left: { style: "none" },
            right: { style: "none" },
            insideHorizontal: { style: "none" },
            insideVertical: { style: "none" },
          },
        })
      );

      // Footer notes will be added to the Footer section below

      // Create document
      const docxDoc = new Document({
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: 1000,
                  right: 1000,
                  bottom: 2000,
                  left: 1000,
                },
              },
            },
            footers: {
              default: (() => {
                const footerChildren = [];

                // Add horizontal line (1/4 of page width) at top of footer
                if (footerNotes.length > 0) {
                  // Use a table to create a partial-width line
                  footerChildren.push(
                    new Table({
                      rows: [
                        new TableRow({
                          children: [
                            new TableCell({
                              width: { size: 25, type: WidthType.PERCENTAGE },
                              borders: {
                                top: {
                                  color: "000000",
                                  size: 6,
                                  style: "single",
                                },
                                bottom: { style: "none" },
                                left: { style: "none" },
                                right: { style: "none" },
                              },
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "",
                                      size: 1,
                                    }),
                                  ],
                                }),
                              ],
                            }),
                          ],
                        }),
                      ],
                      width: { size: 100, type: WidthType.PERCENTAGE },
                      layout: TableLayoutType.FIXED,
                    })
                  );

                  // Add spacing after line
                  footerChildren.push(
                    new Paragraph({
                      spacing: { before: 300, after: 0 },
                    })
                  );

                  // Add footer notes - Calibri 9pt with superscript numbers
                  footerNotes.forEach((noteData) => {
                    const dateStr = noteData.dateElected
                      ? `(${formatDateForFooter(noteData.dateElected)})`
                      : "";
                    const sourceText = `${noteData.source} ${dateStr}`.trim();

                    footerChildren.push(
                      new Paragraph({
                        children: [
                          // Superscript number
                          new TextRun({
                            text: String(noteData.noteNumber),
                            size: 14, // 7pt for superscript
                            font: "Calibri",
                            superScript: true,
                          }),
                          // Source and date
                          new TextRun({
                            text: ` ${sourceText}`,
                            size: 18, // 9pt = 18 half-points
                            font: "Calibri",
                          }),
                        ],
                        spacing: { after: 100 },
                      })
                    );
                  });

                  // Add spacing before page number
                  footerChildren.push(
                    new Paragraph({
                      spacing: { before: 200, after: 0 },
                    })
                  );
                }

                // Add page number at the end
                footerChildren.push(
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Page ",
                        size: 20,
                        font: "Calibri",
                      }),
                      new TextRun({
                        children: [PageNumber.CURRENT],
                        size: 20,
                        font: "Calibri",
                      }),
                      new TextRun({
                        text: " of ",
                        size: 20,
                        font: "Calibri",
                      }),
                      new TextRun({
                        children: [PageNumber.TOTAL_PAGES],
                        size: 20,
                        font: "Calibri",
                      }),
                    ],
                    alignment: AlignmentType.CENTER,
                  })
                );

                return new Footer({
                  children: footerChildren,
                });
              })(),
            },
            children,
          },
        ],
      });

      // Save the document
      const memberName = memberData.houseMember.name.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      );
      const dateStr = new Date().toISOString().split("T")[0];
      const fileName = `Committee_Membership_${memberName}_${dateStr}.docx`;

      // eslint-disable-next-line no-await-in-loop
      const blob = await Packer.toBlob(docxDoc);

      // Add delay to prevent browser from blocking multiple downloads
      if (i > 0) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(() => {
            resolve();
          }, i * 200);
        });
      }

      saveAs(blob, fileName);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    Toast({ type: 4, content: "Failed to generate DOCX file." });
  }
};
